import React from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

// Layouts
import AppLayout from "../layout/AppLayout";
import ClientPortalLayout from "../layout/ClientPortalLayout";
import PublicLayout from "../layout/PublicLayout";

// Pages
import { SuperadminDashboard } from "../views/Superadmin";
import {
  OrganizationDashboard,
  OrganizationSettingsPage,
} from "../views/Organization";
import { CaseDashboard } from "../views/Cases";
import { CreateCasePage } from "../views/Cases/CreateCasePage";
import { AssignUserPage } from "../views/Cases/AssignUserPage";
import { CaseDetailPage } from "../views/Cases/CaseDetailPage";
import { CreateRequestPage } from "../views/Cases/CreateRequestPage";
import { RequestDetailPage } from "../views/Cases/RequestDetailPage";
import { FileManagement } from "../views/Files";
import { UploadFilePage } from "../views/Files/UploadFilePage";
import { RequestTemplatesList } from "../views/RequestTemplates";
import { CreateRequestTemplatePage } from "../views/RequestTemplates/CreateRequestTemplatePage";
import { EditRequestTemplatePage } from "../views/RequestTemplates/EditRequestTemplatePage";
import { FormSubmissionViewPage } from "../views/RequestTemplates/FormSubmissionViewPage";
import { AuditLogDashboard } from "../views/Audit";
import { SettingsPage } from "../views/Settings";
import SignIn from "../views/AuthPages/SignIn";
import SignUp from "../views/AuthPages/SignUp";
import { ResourceDetailPage } from "../views/Resources/ResourceDetailPage";
import { SharedResourcePage } from "../views/Public/SharedResourcePage";
import { PublicRequestPage } from "../views/Public/PublicRequestPage";
import { VerifyMagicLinkPage } from "../views/Public/VerifyMagicLinkPage";
import LandingPage from "../views/Public/LandingPage";
import AboutPage from "../views/Public/AboutPage";
import SecurityPage from "../views/Public/SecurityPage";
import NotFound from "../views/NotFound";
import { MemberDetailPage } from "../views/Organization/MemberDetail";
import { RecipientEmailVerification } from "../components/verification/RecipientEmailVerification";
import { SignupConfirmation } from "../views/AuthPages/SignupConfirmation";
import { VerificationError } from "../components/verification/VerificationError";
import { EmbeddedCeremony } from "../components/signature/EmbeddedCeremony";
import { OrgProvider } from "../contexts/OrgContext";
import { SubmissionsList } from "../views/RequestTemplates/SubmissionsList";

// Client Portal Pages
import { ClientDashboard } from "../views/ClientPortal/ClientDashboard";
import { ClientDocuments } from "../views/ClientPortal/ClientDocuments";

/** Protected Route wrapper */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { currentUser, loading } = useAuth();

  if (loading) return <LoadingScreen />;
  if (!currentUser) return <Navigate to="/login" replace />;

  return <>{children}</>;
};

/** Superadmin-only Route wrapper */
const SuperadminRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isSuperadmin, loading } = useAuth();

  if (loading) return <LoadingScreen />;
  if (!isSuperadmin) return <Navigate to="/cases" replace />;

  return <>{children}</>;
};

/** Organization route wrapper: ensures user has access to orgId */
const OrganizationRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { orgId } = useParams<{ orgId: string }>();
  const { currentUser, impersonatedUser, loading } = useAuth();

  const effectiveUser = impersonatedUser ?? currentUser;

  if (loading) return <LoadingScreen />;

  // If no user, redirect to login instead of 403
  if (!effectiveUser) {
    return <Navigate to="/login" replace />;
  }

  // If user doesn't have access to this org, show 403
  if (!effectiveUser.organizations.some((org) => org.id === orgId)) {
    return <Navigate to="/403" replace />;
  }

  return <>{children}</>;
};

/** Loading placeholder */
const LoadingScreen = () => (
  <div className="flex min-h-screen items-center justify-center bg-white dark:bg-boxdark-2">
    <div className="text-center">
      <div className="mx-auto mb-4 h-16 w-16 animate-spin rounded-full border-4 border-solid border-brand-500 border-t-transparent"></div>
      <p className="text-gray-600 dark:text-gray-400">Loading...</p>
    </div>
  </div>
);

/** Landing Page Route - shows marketing page or redirects authenticated users */
const LandingPageRoute: React.FC = () => {
  const { currentUser, loading } = useAuth();

  if (loading) return <LoadingScreen />;

  // If user is authenticated, redirect to their dashboard
  if (currentUser) {
    if (currentUser.organizations.some((org) => org.role === "client")) {
      return <Navigate to="/portal/dashboard" replace />;
    }
    return (
      <Navigate
        to={`/organizations/${currentUser.organizations[0].id}/cases`}
        replace
      />
    );
  }

  // Otherwise, show the landing page
  return <LandingPage />;
};

/** Main App Router */
export const AppRouter: React.FC = () => {
  const { currentUser } = useAuth();

  const getDefaultRoute = () => {
    if (!currentUser) return "/login";

    if (currentUser.organizations.some((org) => org.role === "client")) {
      return "/portal/dashboard";
    }
    return `/organizations/${currentUser.organizations[0].id}/cases`;
  };

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          !currentUser ? (
            <SignIn />
          ) : (
            <Navigate to={getDefaultRoute()} replace />
          )
        }
      />
      <Route
        path="/signup"
        element={
          !currentUser ? (
            <SignUp />
          ) : (
            <Navigate to={getDefaultRoute()} replace />
          )
        }
      />

      {/* Public routes with Casekeep branding */}
      <Route element={<PublicLayout />}>
        {/* This is for SUBMITTING forms via request-based flow */}
        <Route path="/request/form/:token" element={<PublicRequestPage />} />
        {/* This is for viewing the share link of the file or submission */}
        <Route path="/share/:token" element={<SharedResourcePage />} />
        {/* Magic link (share link) verification */}
        <Route
          path="/share/verify/:verificationToken"
          element={<VerifyMagicLinkPage />}
        />
        {/* Auth confirmation route */}
        <Route path="/auth/confirm" element={<SignupConfirmation />} />
        {/* Request verification routes */}
        <Route
          path="/request/verify/:token"
          element={<RecipientEmailVerification />}
        />
        <Route path="/request/sign/:token" element={<EmbeddedCeremony />} />
        <Route path="/request/expired" element={<VerificationError />} />
      </Route>

      {/* This is an authenticated view to see the submission. Shared by admin and client */}
      <Route
        path="/form-submissions/:submissionId"
        element={
          <ProtectedRoute>
            <FormSubmissionViewPage />
          </ProtectedRoute>
        }
      />

      {/* User settings page */}
      <Route
        element={
          <ProtectedRoute>
            <OrgProvider mode="adminAuth">
              <AppLayout />
            </OrgProvider>
          </ProtectedRoute>
        }
      >
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Client Portal */}
      <Route
        element={
          <ProtectedRoute>
            <OrgProvider mode="clientAuth">
              <ClientPortalLayout />
            </OrgProvider>
          </ProtectedRoute>
        }
      >
        <Route path="/portal/dashboard" element={<ClientDashboard />} />
        <Route path="/portal/documents" element={<ClientDocuments />} />
      </Route>

      {/* Root - Landing page or redirect */}
      <Route path="/" element={<LandingPageRoute />} />

      {/* About page */}
      <Route path="/about" element={<AboutPage />} />

      {/* Security page */}
      <Route path="/security" element={<SecurityPage />} />

      {/* Organization-specific routes */}
      <Route
        path="/organizations/:orgId/*"
        element={
          <OrganizationRoute>
            <OrgProvider mode="adminUrl">
              <AppLayout />
            </OrgProvider>
          </OrganizationRoute>
        }
      >
        {/* Default dashboard for the organization */}
        <Route index element={<OrganizationDashboard />} />

        {/* Nested org routes */}
        <Route path="cases" element={<CaseDashboard />} />
        <Route path="cases/new" element={<CreateCasePage />} />
        <Route path="cases/:caseId" element={<CaseDetailPage />} />
        <Route
          path="cases/:caseId/requests/new"
          element={<CreateRequestPage />}
        />
        <Route
          path="cases/:caseId/requests/:requestId"
          element={<RequestDetailPage />}
        />
        <Route path="cases/assign-user" element={<AssignUserPage />} />

        <Route path="files" element={<FileManagement />} />
        <Route path="files/upload" element={<UploadFilePage />} />
        <Route path="files/:fileId" element={<ResourceDetailPage />} />

        {/* Request Templates */}
        <Route path="request-templates" element={<RequestTemplatesList />} />
        <Route
          path="request-templates/new"
          element={<CreateRequestTemplatePage />}
        />
        <Route
          path="request-templates/:templateId/edit"
          element={<EditRequestTemplatePage />}
        />
        <Route path="form-submissions" element={<SubmissionsList />} />
        <Route
          path="form-submissions/:submissionId"
          element={<ResourceDetailPage />}
        />

        <Route path="members/:userId" element={<MemberDetailPage />} />

        <Route path="audit" element={<AuditLogDashboard />} />

        <Route path="settings" element={<OrganizationSettingsPage />} />
      </Route>

      {/* Superadmin */}
      <Route
        path="/superadmin"
        element={
          <SuperadminRoute>
            <SuperadminDashboard />
          </SuperadminRoute>
        }
      />

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};
